import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useLoader } from '../../contexts/LoaderContext';
import { useSiteVisit } from '../../contexts/SiteVisitContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const CompleteNotes = ({ open, onClose, siteVisit, onSuccess, handleCompleteVisit }) => {
  const [notes, setNotes] = useState(siteVisit?.completion_notes || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { showLoader, hideLoader } = useLoader();
  const { completeSiteVisit } = useSiteVisit();

  const getLeadInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleSubmit = async () => {
    if (!notes.trim()) {
      setError('Please enter completion notes');
      return;
    }

    if (notes.trim().length < 20) {
      setError('Completion notes must be at least 20 characters long');
      return;
    }

    setIsSubmitting(true);
    showLoader();

    try {
      // Call the completeSiteVisit function from context
      if (siteVisit) {
        await completeSiteVisit(siteVisit.id, notes.trim());
      }
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error completing site visit:', error);
      setError('Failed to complete site visit. Please try again.');
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  const handleClose = () => {
    setNotes(siteVisit?.completion_notes || '');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Mark Site Visit Completed
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

         {/* Current Visit Details - Redesigned */}
         <Box sx={{ 
          mb: 4,
          p: 3,
          borderRadius: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          {/* Status Chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Status:
            </Typography>
            <Chip 
              label={siteVisit?.status_verbose || 'Unknown'} 
              size="small"
              color="primary"
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: '20px'
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', mt: 2, gap: 3, flexWrap: 'wrap' }}>
            {/* Lead Information */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
              minWidth: 200
            }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
                fontSize: '0.875rem'
              }}>
                {getLeadInitials(siteVisit?.lead?.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {siteVisit?.lead?.name || 'Unknown Lead'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <PhoneIcon fontSize="small" color="action" sx={{ fontSize: '0.75rem' }} />
                  <Typography variant="caption" color="text.secondary">
                    {siteVisit?.lead?.mobile || siteVisit?.lead?.contact || 'No contact'}
                  </Typography>
                </Box>
                {siteVisit?.lead?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                    <EmailIcon fontSize="small" color="action" sx={{ fontSize: '0.75rem' }} />
                    <Typography variant="caption" color="text.secondary">
                      {siteVisit.lead.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Project Information */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
              minWidth: 200
            }}>
              <BusinessIcon color="primary" sx={{ fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {siteVisit?.project?.name || 'Unknown Project'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Property Visit
                </Typography>
              </Box>
            </Box>

            {/* Current Schedule */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
              minWidth: 200
            }}>
              <ScheduleIcon color="warning" sx={{ fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {siteVisit?.scheduled_date ? 
                    dayjs(siteVisit.scheduled_date).tz('Asia/Kolkata').format('MMM DD, YYYY') : 
                    'Not scheduled'
                  }
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {siteVisit?.scheduled_date ? 
                    dayjs(siteVisit.scheduled_date).tz('Asia/Kolkata').format('h:mm A') : 
                    'No time set'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />
        
        <Box>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            mb: 1.5
          }}>
            Completion Notes
          </Typography>
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={6}
            fullWidth
            placeholder="Please provide detailed notes about the site visit completion (minimum 20 characters)..."
            error={notes.trim().length > 0 && notes.trim().length < 20}
            helperText={
              notes.trim().length > 0 && notes.trim().length < 20
                ? `Please provide at least 20 characters (${notes.trim().length}/20)`
                : `${notes.trim().length}/20 characters minimum`
            }
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem'
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Include details about the visit outcome, customer feedback, next steps, or any other relevant information.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          color="inherit"
          disabled={isSubmitting}
          sx={{ 
            textTransform: 'none',
            borderRadius: 2,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="success"
          disabled={isSubmitting || !notes.trim() || notes.trim().length < 20}
          startIcon={isSubmitting ? null : <CheckCircleIcon />}
          sx={{ 
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            fontWeight: 600
          }}
        >
          {isSubmitting ? 'Completing...' : 'Complete Visit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteNotes; 